import { Helmet } from 'react-helmet-async';

export interface ArticleSeoMeta {
  publishedTime: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
}

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string[];
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  twitterCard?: 'summary' | 'summary_large_image';
  structuredData?: object | object[];
  articleMeta?: ArticleSeoMeta;
  noIndex?: boolean;
  priority?: 'high' | 'normal' | 'low';
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  structuredData,
  articleMeta,
  noIndex = false,
  priority = 'normal'
}: SEOHeadProps) {
  const fullTitle = `${title} | Memara - AI Memory Platform`;
  
  // Environment-aware base URL (Vite best practice)
  const baseUrl = import.meta.env.PROD 
    ? 'https://memara.io'
    : import.meta.env.VITE_APP_URL || 'http://localhost:3000';
  
  // Default OG image with environment awareness
  const defaultOgImage = `${baseUrl}/og-image.jpg`;
  const finalOgImage = ogImage || defaultOgImage;
  
  // Smart canonical URL handling - improved logic
  const fullCanonicalUrl = canonicalUrl || (
    typeof window !== 'undefined' 
      ? window.location.href 
      : baseUrl
  );
  
  // Performance hints based on priority
  const performanceHints = priority === 'high' ? (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="dns-prefetch" href="https://api.memara.io" />
    </>
  ) : null;
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(', ')} />
      <meta name="robots" content={noIndex ? 'noindex,nofollow' : 'index,follow,max-image-preview:large'} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="author" content="Memara" />
      <meta name="generator" content="Vite" />
      
      {/* Performance & Security */}
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      <meta name="format-detection" content="telephone=no" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullCanonicalUrl} />
      
      {/* Performance Hints */}
      {performanceHints}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={finalOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${title} - Memara AI Memory Platform`} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:site_name" content="Memara" />
      <meta property="og:locale" content="en_US" />

      {/* Open Graph — article (blog posts) */}
      {ogType === 'article' && articleMeta?.publishedTime ? (
        <>
          <meta property="article:published_time" content={articleMeta.publishedTime} />
          {articleMeta.modifiedTime ? (
            <meta property="article:modified_time" content={articleMeta.modifiedTime} />
          ) : null}
          {articleMeta.section ? (
            <meta property="article:section" content={articleMeta.section} />
          ) : null}
          {(articleMeta.tags || []).map((tag, i) => (
            <meta key={`${tag}-${i}`} property="article:tag" content={tag} />
          ))}
        </>
      ) : null}
      
      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={finalOgImage} />
      <meta name="twitter:image:alt" content={`${title} - Memara AI Memory Platform`} />
      <meta name="twitter:site" content="@memaraio" />
      <meta name="twitter:creator" content="@memaraio" />
      
      {/* Additional SEO Tags */}
      <meta name="theme-color" content="#6366f1" />
      <meta name="msapplication-TileColor" content="#6366f1" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      
      {/* Modern Favicon System using brand assets */}
      <link rel="icon" type="image/png" sizes="16x16" href="/logos/dark/memara_logo_16x16.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/logos/dark/memara_logo_32x32.png" />
      <link rel="icon" type="image/png" sizes="48x48" href="/logos/dark/memara_logo_48x48.png" />
      <link rel="apple-touch-icon" sizes="180x180" href="/logos/dark/memara_logo_256x256.png" />
      <link rel="apple-touch-icon" sizes="512x512" href="/logos/dark/memara_logo_512x512.png" />
      
      {/* Structured Data (single object or array of graphs) */}
      {structuredData
        ? (Array.isArray(structuredData) ? structuredData : [structuredData]).map(
            (graph, index) => (
              <script key={index} type="application/ld+json">
                {JSON.stringify(graph)}
              </script>
            )
          )
        : null}
      
      {/* Enhanced Structured Data for Organization */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Memara",
          "description": "AI Memory Platform for Intelligent Information Management",
          "url": baseUrl,
          "logo": `${baseUrl}/logos/memara-logo.svg`,
          "sameAs": [
            "https://twitter.com/memaraio",
            "https://www.linkedin.com/company/memaraio/",
            "https://github.com/memaraio"
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "customer service",
            "url": `${baseUrl}/contact`
          }
        })}
      </script>
    </Helmet>
  );
} 